import React, { useState } from 'react';
import { CreateFlowModal } from './CreateFlowModal';
import { ModalContent } from './shared/FormComponents';
import { CreateFlowBackButton } from './shared/CreateFlowBackButton';
import { SmartCreateForm, DifficultyLevel } from './shared/SmartCreateForm';
import { CreateFlowStepIndicator } from './shared/CreateFlowStepIndicator';
import { AttachToCourseModal } from './AttachToCourseModal';
import { GoalType, ISet } from 'models/set';
import { getContentTypeDisplayName } from './shared/contentTypeUtils';
import { useCreateSmartAssignment } from 'hooks/useCreateSmartAssignment';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { AnalyticsService } from 'utils/AnalyticsService';

interface IProps {
  goalType: GoalType;
  onClose: () => void;
  onBack: () => void;
  onSetCreated?: () => void;
}

type ContentCreationStep = 'smart-create' | 'attach-to-course';

export const SmartCreateContentModal = (props: IProps) => {
  const { goalType, onClose, onBack, onSetCreated } = props;
  const [currentStep, setCurrentStep] = useState<ContentCreationStep>('smart-create');
  const [topic, setTopic] = useState<string>('');
  const [difficulty, setDifficulty] = useState<DifficultyLevel>('Beginner');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [createdSet, setCreatedSet] = useState<ISet | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const user = useCurrentUser();
  const [createSmartAssignment] = useCreateSmartAssignment();

  const handleGenerateContent = async () => {
    if (!topic.trim() || !user?.primaryPartner.id) return;

    setIsCreating(true);
    try {
      const result = await createSmartAssignment({
        title: topic,
        partnerId: user.primaryPartner.id,
        level: difficulty,
        goal_type: goalType,
        // Note: courseId is optional and not provided here since this is standalone content creation
      });

      setCreatedSet(result);

      AnalyticsService.getInstance().track('creation_completed', {
        creation_type: 'content',
        content_type: goalType,
        creation_method: 'smart_create',
      });

      setCurrentStep('attach-to-course');
      onSetCreated?.();
    } catch (error) {
      console.error('Error generating smart content:', error);
      setIsCreating(false);
    }
  };

  const handleBackToSmartCreate = () => {
    setCurrentStep('smart-create');
  };

  const handleDone = () => {
    onClose();
  };

  //TODO: This can be used once the backend supports it
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
    }
  };

  if (currentStep === 'attach-to-course') {
    if (!createdSet) {
      throw new Error('Created set is null when trying to attach to course');
    }
    return (
      <AttachToCourseModal
        onClose={onClose}
        onBack={handleBackToSmartCreate}
        onDone={handleDone}
        contentItem={createdSet}
      />
    );
  }

  const contentTypeDisplayName = getContentTypeDisplayName(goalType);

  return (
    <CreateFlowModal title={`Create ${contentTypeDisplayName} (Smart Create)`} onClose={onClose}>
      <ModalContent>
        <CreateFlowBackButton onClick={onBack} />
        <CreateFlowStepIndicator activeStep={2} />

        <SmartCreateForm
          topic={topic}
          difficulty={difficulty}
          // pdfFile={pdfFile}
          onTopicChange={setTopic}
          onDifficultyChange={setDifficulty}
          // onFileUpload={handleFileUpload}
          onSubmit={handleGenerateContent}
          isCreating={isCreating}
          submitButtonText={`Generate ${contentTypeDisplayName}`}
          topicPlaceholder={`E.g. JavaScript fundamentals`}
        />
      </ModalContent>
    </CreateFlowModal>
  );
};
